import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Award, DollarSign, Clock, ChevronDown } from "lucide-react";
import ApplyCta from "./ApplyCta";

const pillars = [
  {
    icon: Award,
    title: "Prestigio",
    subtitle: "Elevación de Valor",
    description:
      "Blindamos la reputación y elevamos la percepción de valor para que tu empresa ocupe el lugar de reconocimiento que le pertenece.",
    points: ["Posicionamiento", "Percepción", "Proyección", "Prestigio"],
  },
  {
    icon: DollarSign,
    title: "Dinero",
    subtitle: "Escalabilidad",
    description:
      "Los números marcan la jugada. Trazamos la hoja de ruta para una expansión sólida que proyecte crecimiento en el mercado.",
    points: ["Modelo de Valor", "Ventas", "Diversificación", "Rentabilidad"],
  },
  {
    icon: Clock,
    title: "Tiempo",
    subtitle: "Ingeniería de Procesos",
    description:
      "Valoramos el tiempo como tu activo más escaso. Diseñamos la arquitectura de sistemas que optimicen tu operatividad.",
    points: ["Sistematización", "Optimización", "Planificación", "Calendarización"],
  },
];

const Solution = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section
      className="py-24 md:py-32 relative overflow-hidden text-white"
      id="solucion"
    >
      <div className="absolute inset-0" style={{ background: "#1d4a8c" }} />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#7ab7fd]/15 rounded-full blur-3xl" />

      <div className="container px-4 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto mb-20"
        >
          <span className="text-sm font-semibold tracking-widest uppercase mb-4 block text-[#7ab7fd]">
            Marcas Valiosas, Negocios Visionarios
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white">
            Arquitectura de <span className="text-[#7ab7fd]">Reputación©</span>
          </h2>
          <p className="text-lg text-white/60 max-w-3xl mx-auto">
            Diseñamos el ecosistema estratégico que permite a empresas consolidadas evolucionar hacia
            Marcas referentes.
          </p>
        </motion.div>

        {/* Pillars grid - collapsible cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16">
          {pillars.map((pillar, index) => {
            const isOpen = openIndex === index;
            return (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15 * index }}
                className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-sm overflow-hidden cursor-pointer"
                onClick={() => toggle(index)}
              >
                {/* Always visible header */}
                <div className="p-8 pb-4 flex flex-col gap-4">
                  <div className="w-14 h-14 rounded-xl bg-white/15 flex items-center justify-center">
                    <pillar.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-display text-2xl font-bold text-white">{pillar.title}</h3>
                      <p className="text-sm text-[#7ab7fd] font-medium mt-1">{pillar.subtitle}</p>
                    </div>
                    <motion.div
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-1"
                    >
                      <ChevronDown className="w-5 h-5 text-white/50" />
                    </motion.div>
                  </div>
                </div>

                {/* Collapsible content */}
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-8 pb-8 pt-2 flex flex-col gap-4">
                        <p className="text-white/60 text-base leading-relaxed">{pillar.description}</p>
                        <ul className="space-y-2">
                          {pillar.points.map((point) => (
                            <li key={point} className="flex items-center gap-2 text-sm text-white/80">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#7ab7fd]/60" />
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        <ApplyCta variant="dark" />
      </div>
    </section>
  );
};

export default Solution;
